import { Box, Typography } from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { fDate, fDateTime } from 'src/utils/format-time';
import { useMemo } from 'react';
import { useTranslate } from 'src/locales';
import {
  TimelineDrawer,
  type HeaderConfig,
  type TimelineConfig,
  type OverviewConfig,
  type TimelineItemData,
  type ActionButtonConfig,
} from 'src/components/drawer/TimelineDrawer';
import type { IPayout } from 'src/store/slices/paymentsAPISlice';
import { useUpdatePayoutMutation } from 'src/store/slices/paymentsAPISlice';
import { toast } from 'src/components/snackbar';

type Props = {
  open: boolean;
  onClose: () => void;
  payouts: IPayout[];
  balance: string;
  from: string;
  to: string;
  hasRequestedPayout?: boolean;
  type: 'service' | 'client';
  currency: string;
  reference?: string;
  clientId: string;
  onPayoutUpdated?: () => void;
};

export function PayoutHistoryDrawer({
  open,
  onClose,
  payouts,
  balance,
  from,
  to,
  hasRequestedPayout = false,
  type,
  currency,
  reference,
  clientId,
  onPayoutUpdated,
}: Props) {
  const { t } = useTranslate();
  const [updatePayout, { isLoading }] = useUpdatePayoutMutation();

  const handleMarkAsDone = async () => {
    if (!reference) return;

    try {
      const response = await updatePayout({
        payoutId: reference,
        data: {
          clientId,
          confirmedOn: new Date().toISOString(),
        },
      }).unwrap();

      if (!response.success) {
        throw new Error('Failed to update payout');
      }

      toast.success(
        t(
          response.result.payout.type === 'client'
            ? 'toastMessages.payoutMarkedAsDone'
            : 'toastMessages.payinMarkedAsDone'
        ),
        { position: 'bottom-left' }
      );

      onPayoutUpdated?.();
      onClose();
    } catch (error: any) {
      console.error('Error marking payout as done:', error);

      const errorMessage =
        error?.data?.error || error?.message || t('toastMessages.payoutMarkAsDoneFailed');
      toast.error(errorMessage, { position: 'bottom-left' });
    }
  };

  const timelineItems: TimelineItemData[] = useMemo(() => {
    const items: TimelineItemData[] = [];

    payouts.forEach((payout) => {
      if (payout.createdAt) {
        items.push({
          id: `${payout.id}-initiated`,
          status: payout.type === 'client' ? 'payoutInitiated' : 'payinInitiated',
          timestamp: `${fDate(payout.createdAt, 'YYYY-MM-DD')} | ${fDateTime(payout.createdAt, 'HH:mm:ss')}`,
          originalTimestamp: payout.createdAt,
          amount: payout.total ? `${payout.total} ${currency}` : undefined,
        });
      }

      if (payout.confirmedOn !== null) {
        items.push({
          id: `${payout.id}-confirmed`,
          status: payout.type === 'client' ? 'payoutConfirmed' : 'payinConfirmed',
          timestamp: `${fDate(payout.confirmedOn, 'YYYY-MM-DD')} | ${fDateTime(payout.confirmedOn, 'HH:mm:ss')}`,
          originalTimestamp: payout.confirmedOn,
          amount: payout.total ? `${payout.total} ${currency}` : undefined,
        });
      }
    });

    return items.sort((a, b) => {
      const dateA = new Date(a.originalTimestamp || '0000-00-00 00:00:00');
      const dateB = new Date(b.originalTimestamp || '0000-00-00 00:00:00');
      return dateB.getTime() - dateA.getTime();
    });
  }, [payouts, currency]);

  // Header configuration
  const headerConfig: HeaderConfig = {
    title: (
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1C252E' }}>
        {t('common.details')}
      </Typography>
    ),
  };

  // Overview configuration
  const overviewConfig: OverviewConfig = {
    sections: [
      {
        label: 'Balance',
        value: balance,
      },
      {
        label: 'From',
        value: from,
      },
      {
        label: 'To',
        value: to,
      },
    ],
    containerSx: {
      backgroundColor: '#F4F6F8',
      p: 2,
    },
  };

  // Custom timeline item renderer for payout-specific logic
  const payoutItemRenderer = (item: TimelineItemData) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 0.5,
      }}
    >
      <Box>
        <Typography variant="subtitle2" fontWeight={500}>
          {t(`payout.${item.status}`)}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          {item.timestamp}
        </Typography>
      </Box>

      {item.amount && (
        <Box
          sx={{
            backgroundColor: item.id.includes('confirmed') ? 'success.main' : '#1C252E',
            color: 'white',
            borderRadius: '6px',
            px: 1.5,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontWeight: 700,
            fontSize: '12px',
          }}
        >
          {item.id.includes('confirmed') && <Iconify icon="lucide:check" width={12} />}
          {item.amount}
        </Box>
      )}
    </Box>
  );

  // Timeline configuration
  const timelineConfig: TimelineConfig = {
    items: timelineItems,
    title: (
      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
        {t('common.history')}
      </Typography>
    ),
    backgroundColor: '#F4F6F8',
    itemSpacing: 0.5,
    showConnectors: true,
    dotColor: (item) => (item.id.includes('confirmed') ? 'success' : 'warning'),
    dotSx: (item) => ({
      borderWidth: 2,
      borderColor: item.id.includes('confirmed') ? '#4CAF50' : '#FF9800',
      backgroundColor: 'transparent',
    }),
    connectorSx: {
      backgroundColor: '#E0E0E0',
      minHeight: '6px',
      maxHeight: '6px',
    },
    renderItem: payoutItemRenderer,
  };

  // Action button configuration
  const actionButtonConfig: ActionButtonConfig | undefined =
    hasRequestedPayout && reference
      ? {
          label: t('common.markAsDone'),
          onClick: handleMarkAsDone,
          loading: isLoading,
          startIcon: 'lucide:check',
          backgroundColor: '#F16437',
          hoverColor: '#E55A2B',
          fullWidth: true,
          variant: 'contained',
        }
      : undefined;

  return (
    <TimelineDrawer
      open={open}
      onClose={onClose}
      width={400}
      maxWidth="90vw"
      padding={0}
      header={headerConfig}
      showHeaderDivider={false}
      overview={overviewConfig}
      showOverviewDivider
      timeline={timelineConfig}
      actionButton={actionButtonConfig}
      showActionButton={!!actionButtonConfig}
      scrollable
      maxHeight="85vh"
      showDivider
    />
  );
}
